/* eslint-disable import/no-extraneous-dependencies */
import { Grid } from '@mui/material';
import { BasicButton } from '@tassat/button';
import {
    DatePicker,
    RadioField,
    RichTextField,
    SimpleErrorMessage
} from '@tassat/formik-input';
import { ActionTypes, ClientMessagesTypeEnum, RadioOption } from '@tassat/ui-definitions';
import { Formik, FormikProps } from 'formik';
import moment from 'moment';
import * as React from 'react';

import { validateClientMessage } from './submission';
import { IFormProps, IFormValues, IFormikProps } from './types';

const FormContents = (props: IFormProps) => {
    const {
        classes,
        values,
        errors,
        submitForm,
        isValid,
        isSubmitting,
        dirty,
        submitError,
        setFieldValue,
        setFieldTouched,
        initialValues
    } = props;

    const messageTypeOptions: RadioOption[] = React.useMemo(() => [
        {
            label: 'Banner',
            value: ClientMessagesTypeEnum.BANNER
        },
        {
            label: 'Advisory',
            value: ClientMessagesTypeEnum.ADVISORY
        }
    ], []);

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='column'
                spacing={0}
            >
                <Grid
                    container
                    direction='row'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        container
                        direction='column'
                        item
                        spacing={0}
                        wrap='nowrap'
                        xs={6}
                    >
                        <Grid
                            item
                            xs={12}
                        >
                            <RadioField
                                errors={errors}
                                label='Type'
                                name='ClientMsgType'
                                options={messageTypeOptions}
                                values={values}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={10}
                        >
                            <DatePicker
                                errors={errors}
                                label='Start Date & Time'
                                name='StartAt'
                                withTime
                            />
                        </Grid>
                        <Grid
                            item
                            xs={10}
                        >
                            <DatePicker
                                disabled={Boolean(errors.StartAt)}
                                errors={errors}
                                label='End Date & Time'
                                name='EndAt'
                                withTime
                            />
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        direction='column'
                        item
                        spacing={0}
                        xs={6}
                    >
                        <RichTextField
                            errors={errors}
                            initialValue={initialValues.Message}
                            label='Message'
                            name='Message'
                            setFieldTouched={setFieldTouched}
                            setFieldValue={setFieldValue}
                        />
                    </Grid>
                </Grid>
                <Grid
                    alignItems='center'
                    className={classes.submitButtonContainer}
                    container
                    item
                    justifyContent='space-evenly'
                    xs={12}
                >
                    <Grid
                        className={classes.submitButton}
                        container
                        direction='column'
                        item
                    >
                        <BasicButton
                            disabled={!dirty ||
                                isSubmitting ||
                                !isValid}
                            onClick={submitForm}
                            text='Submit'
                            type='submit'
                        />
                        <SimpleErrorMessage
                            className={classes.submitError}
                            error={submitError}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
};

export default function ClientMessagesForm(props: IFormikProps) {
    const {
        classes,
        onClose,
        formProps
    } = props;

    const {
        validationMessages,
        actionType,
        editMessage,
        submitClientMessage
    } = formProps;

    const [ submitError, setSubmitError ] = React.useState<string>('');

    let initialValues = {} as IFormValues;
    if (actionType === ActionTypes.add) {
        initialValues = {
            ClientMsgType: -1,
            StartAt: '',
            EndAt: '',
            Message: ''
        };
    } else if (actionType === ActionTypes.edit) {
        if (!editMessage) return <div />;
        const start = moment.unix(Number(editMessage.StartAt) / 1000);
        const end = moment.unix(Number(editMessage.EndAt) / 1000);

        initialValues = {
            ClientMsgType: editMessage.ClientMsgType,
            StartAt: `${start.format('YYYY-MM-DD')}T${start.format('HH:mm')}`,
            EndAt: `${end.format('YYYY-MM-DD')}T${end.format('HH:mm')}`,
            Message: editMessage.Message
        };
    }

    const clientMessagesOriginalValidations = (values: IFormValues) => validateClientMessage(values, initialValues, {
        actionType,
        editMessage,
        validationMessages
    });

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                item
                sm={12}
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values: IFormValues) => {
                        await submitClientMessage(values, {
                            actionType,
                            editMessageId: editMessage?.MessageId || '',
                            setSubmitError,
                            onClose
                        });
                    }}
                    validate={clientMessagesOriginalValidations}
                    validateOnChange
                    validateOnMount
                >
                    {(formContentsProps: FormikProps<IFormValues>) => (
                        <FormContents
                            {...formContentsProps}
                            classes={classes}
                            formProps={formProps}
                            submitError={submitError}
                        />
                    )}
                </Formik>
            </Grid>
        </Grid>
    );
}